<template>
	<div
		class="p-6 transition-all duration-500 border shadow-xl sm:p-8 lg:p-10 sm:shadow-2xl bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl border-white/40 hover:shadow-blue-500/10 animate-fade-in-up"
	>
		<div class="mb-6 text-center sm:mb-8">
			<h2 class="mb-3 text-2xl font-bold sm:text-3xl text-slate-900">
				Available Commercial Quotations
			</h2>
			<p class="max-w-2xl mx-auto text-base sm:text-lg text-slate-600">
				Choose the perfect insurance product for your commercial needs
			</p>
		</div>

		<div
			v-if="products && products.length > 0"
			class="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3"
		>
			<div
				v-for="product in products"
				:key="product.id"
				:class="[
					'relative overflow-hidden transition-all duration-500 bg-white border shadow-sm group rounded-xl p-5 sm:p-6 flex flex-col justify-between',
					selectedProduct && selectedProduct.id === product.id
						? 'border-blue-600 shadow-md ring-2 ring-blue-600/20'
						: 'border-slate-100 hover:border-slate-200',
				]"
			>
				<div>
					<h4
						class="mb-2 text-xl font-bold sm:text-2xl text-slate-900"
					>
						{{ product.name }}
					</h4>
					<p
						class="mb-4 text-3xl font-extrabold text-blue-600 sm:text-4xl"
					>
						KES {{ product.premium.toLocaleString() }}
					</p>
					<ul
						class="pl-5 space-y-2 text-base list-disc sm:text-lg text-slate-600"
					>
						<li
							v-for="(feature, index) in product.features"
							:key="index"
						>
							{{ feature }}
						</li>
					</ul>
				</div>
				<div class="mt-6">
					<button
						@click="openDetailsModal(product)"
						class="w-full px-5 py-3 mb-2 text-base font-semibold text-blue-600 transition-colors duration-300 border-2 border-blue-600 rounded-full sm:px-6 sm:py-3 hover:bg-blue-50"
					>
						View Details
					</button>
					<button
						@click="selectProduct(product)"
						:class="[
							'w-full px-5 py-3 sm:px-6 sm:py-3 rounded-full text-white font-semibold shadow-md transition-colors duration-300',
							selectedProduct && selectedProduct.id === product.id
								? 'bg-blue-700'
								: 'bg-blue-600 hover:bg-blue-700',
						]"
					>
						{{
							selectedProduct && selectedProduct.id === product.id
								? 'Selected'
								: 'Select Plan'
						}}
					</button>
				</div>
				<div
					v-if="selectedProduct && selectedProduct.id === product.id"
					class="absolute text-blue-600 top-4 right-4"
				>
					<svg
						class="w-5 h-5 sm:w-6 sm:h-6"
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path
							fill-rule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
							clip-rule="evenodd"
						></path>
					</svg>
				</div>
			</div>
		</div>
		<div v-else class="py-10 text-center sm:py-12 text-slate-600">
			<p class="text-lg sm:text-xl">
				No quotations available for the selected criteria. Please try
				again with different values.
			</p>
		</div>

		<div
			class="flex flex-col-reverse justify-between gap-4 mt-8 sm:flex-row sm:mt-10"
		>
			<button
				@click="previous"
				class="w-full px-6 py-3 text-base font-semibold text-blue-600 transition-colors duration-300 border-2 border-blue-600 rounded-full sm:w-auto sm:text-lg hover:bg-blue-50"
			>
				Back
			</button>
			<button
				@click="next"
				:disabled="!selectedProduct"
				:class="[
					'w-full sm:w-auto px-6 py-3 text-base sm:text-lg rounded-full text-white font-semibold shadow-lg transition-colors duration-300',
					selectedProduct
						? 'bg-blue-600 hover:bg-blue-700'
						: 'bg-gray-400 cursor-not-allowed',
				]"
			>
				Continue
			</button>
		</div>

		<div
			v-if="isModalOpen"
			class="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-auto bg-gray-900 bg-opacity-75"
		>
			<div
				class="relative w-full max-w-xl mx-auto my-6 bg-white rounded-lg shadow-xl animate-scale-up"
			>
				<div class="p-6">
					<div
						class="flex items-center justify-between pb-3 border-b"
					>
						<h3 class="text-xl font-bold text-gray-900 sm:text-2xl">
							{{ modalProduct.name }} Details
						</h3>
						<button
							@click="closeDetailsModal"
							class="text-gray-400 hover:text-gray-500"
						>
							<svg
								class="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								></path>
							</svg>
						</button>
					</div>
					<div class="py-4">
						<p class="mb-4 text-base text-gray-600">
							{{ modalProduct.description }}
						</p>
						<h4 class="mb-2 text-lg font-semibold text-gray-800">
							Key Features:
						</h4>
						<ul
							class="pl-5 space-y-2 text-base text-gray-600 list-disc"
						>
							<li
								v-for="(detail, index) in modalProduct.details"
								:key="index"
							>
								{{ detail }}
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'CommercialProductQuotation',
	data() {
		return {
			products: [
				{
					id: 1,
					name: 'Standard Commercial',
					premium: 120000,
					features: [
						'Third Party Liability',
						'Fire & Theft',
						'Accidental Damage',
					],
					description:
						'A standard comprehensive plan for commercial vehicles.',
					details: [
						'Comprehensive coverage for commercial use',
						'Includes liability for passengers and goods',
						'Roadside assistance',
					],
				},
				{
					id: 2,
					name: 'Executive Commercial',
					premium: 180000,
					features: [
						'All features of Standard',
						'Excess Protector',
						'Political Violence & Terrorism',
					],
					description:
						'An enhanced plan with additional coverage for political and terrorism risks.',
					details: [
						'All Standard features',
						'No-excess policy on claims',
						'Coverage for political violence, terrorism, and strikes',
					],
				},
				{
					id: 3,
					name: 'Third Party Commercial',
					premium: 45000,
					features: ['Third Party Liability'],
					description:
						'Basic third-party liability cover as required by law.',
					details: [
						'Mandatory third-party liability coverage',
						'Covers bodily injury and property damage to others',
						'Does not cover own vehicle damage',
					],
				},
			],
			selectedProduct: null,
			isModalOpen: false,
			modalProduct: null,
		};
	},
	methods: {
		selectProduct(product) {
			this.selectedProduct = product;
		},
		openDetailsModal(product) {
			this.modalProduct = product;
			this.isModalOpen = true;
		},
		closeDetailsModal() {
			this.isModalOpen = false;
			this.modalProduct = null;
		},
		next() {
			if (this.selectedProduct) {
				this.$emit('next', { selectedProduct: this.selectedProduct });
			}
		},
		previous() {
			this.$emit('previous');
		},
	},
};
</script>

<style scoped>
/* Scoped styles for this component */
</style>
